import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { sendToAnalytics } from "lib/googleAnalytics";
import * as DeviceStates from "lib/devices/wrappers/DeviceStates";
import DeviceReconnectionToast from "components/DeviceReconnectionToast";
import { getDeviceDisplayName } from "lib/devices/connect";
import { translations } from "i18n";
import CustomToast from "components/CustomToast";
import iconConnected from "images/i-tick-connected.svg";
import infoRedIcon from "images/icons/info-red-icon.svg";
import { showModalTrigger } from "containers/ToysPage";

const LOCAL_STORAGE_KEY = "devices";

const connectWithTimeout = (promise, ms) => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error("Timeout")), ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() =>
    clearTimeout(timeoutId)
  );
};

export const onGattServerDisconnected = async (objRef) => {
  await new Promise((r) => setTimeout(r, 100));
  const deviceName = getDeviceDisplayName(objRef.device.name);

  if (objRef.connectionState === DeviceStates.MANUALLY_DISCONNECTED) {
    objRef.setConnectionState(DeviceStates.DISCONNECTED);
    return;
  }

  objRef.setConnectionState(DeviceStates.DISCONNECTED);

  Sentry.captureException(
    new Error(`OnGATTServerDisconnected - ${deviceName}`)
  );
  sendToAnalytics("OnGATTServerDisconnected", "device", deviceName);

  if (document.visibilityState === "hidden") {
    const notification = new Notification(
      translations("Notifications.DeviceWasDisconnected", { deviceName }),
      {
        body: translations("Notifications.TryingToReconnect"),
        icon: "/favicon.ico",
      }
    );
    setTimeout(() => {
      notification.close();
    }, 10000);
  }

  const toastId = toast(
    <CustomToast
      title={translations("Notifications.ToyIsDisconnected")}
      color="#C83737"
      icon={infoRedIcon}
      description={translations("Notifications.TryingToReconnect")}
    />,
    {
      autoClose: 10000,
      className: "tostify_custom_container error_toast",
    }
  );

  // There is a bug from chromium where we cannot reconnect to the device after ~2,5 minutes of attempting
  // we need to reload the page
  // see details here https://bugs.chromium.org/p/chromium/issues/detail?id=681435
  // if there's connected devices - show modal instead refresh
  const refreshPageAfter2Minutes = setTimeout(() => {
    if (window.connectedDevices.length) {
      window.setShowModal(true);
    } else {
      window.location.reload();
    }
  }, 140 * 1000);

  try {
    await connectWithTimeout(objRef.connect(), 140 * 1000);
    objRef.setConnectionState(DeviceStates.CONNECTED);

    if (document.visibilityState === "hidden") {
      const notification = new Notification(
        translations("Notifications.DeviceIsConnected", { deviceName }),
        { icon: "/favicon.ico" }
      );
      setTimeout(() => {
        notification.close();
      }, 5000);
    }

    toast.update(toastId, {
      render: () => (
        <CustomToast
          title={translations("Notifications.DeviceIsConnected", {
            deviceName,
          })}
          color="#83B059"
          icon={iconConnected}
          description={translations("Notifications.YourDeviceIsConnected")}
        />
      ),
      autoClose: 5000,
      className: "tostify_custom_container success_toast",
    });
    clearTimeout(refreshPageAfter2Minutes);
  } catch (err) {
    objRef.setConnectionState(DeviceStates.DISCONNECTED);

    if (document.visibilityState === "hidden") {
      const notification = new Notification(
        translations("Notifications.DeviceWasDisconnected", { deviceName }),
        {
          body: translations("Notifications.TryToReconnectManually"),
          icon: "/favicon.ico",
        }
      );
      setTimeout(() => {
        notification.close();
      }, 5000);
    }

    toast(
      <CustomToast
        title={translations("Notifications.DeviceWasDisconnected", {
          deviceName,
        })}
        color="#C83737"
        icon={infoRedIcon}
        description={translations("Notifications.TryToReconnectManually")}
      />,
      {
        closeButton: true,
        autoClose: 5000,
        className: "tostify_custom_container error_toast",
      }
    );
  }
};
